import type { CombatEvent, DamageType, PendingReaction } from "./types/combat.js";
import type { DifficultyLevel } from "./combat/difficulty.js";
import type { ConditionId, EnemyFaction } from "./types/entity.js";
import type { InkVariableChange } from "./types/narrative.js";
import type { Position, Direction } from "./types/grid.js";

// ---------------------------------------------------------------------------
// Namespace registry
//
// Every event key must use one of these prefixes so callers can filter by
// domain. New namespaces: add the prefix here, then add events to
// EngineEvents below. See specs/core-services.md for ownership table.
// ---------------------------------------------------------------------------

export type EventNamespace =
  | "combat"     // owner: combat-physical
  | "spell"      // owner: combat-spells       (reserved — no events yet)
  | "narrative"  // owner: npcs-and-dialogue
  | "inventory"  // owner: inventory-and-economy (reserved — no events yet)
  | "world"      // owner: world-and-progression
  | "enemy"      // owner: enemies-and-ai        (reserved — no events yet)
  | "perception" // owner: enemies-and-ai        (reserved — no events yet)
  | "ui"         // owner: engine/ui boundary
  | "save";      // owner: persistence           (reserved — no events yet)

// ---------------------------------------------------------------------------
// Event catalogue
//
// Add new events here. Use the namespace prefix that matches EventNamespace.
// Payload types live in src/engine/types/; use Record<string, never> for
// zero-payload events rather than void (makes the generic constraints simpler).
// ---------------------------------------------------------------------------

export interface EngineEvents {
  // ── combat:* ──────────────────────────────────────────────────────────────
  // Emitted by: CombatManager, reactionQueue
  // Consumed by: PartyBar (refresh HP), CombatLog, ReactionPrompt

  /** Full outcome of one attack roll (hit/miss, damage, crit, fumble). */
  "combat:attack": CombatEvent;

  /** Opens the timed reaction prompt UI for an incoming attack. */
  "combat:reaction_window": PendingReaction;

  /** Closes the reaction prompt; carries whether the reaction was spent. */
  "combat:reaction_resolved": { eventId: string; used: boolean };

  /** Fired when any entity's HP reaches 0. UI and main.tsx handle cleanup. */
  "combat:entity_died": { instanceId: string };

  /**
   * An entity took damage. Emitted after HP is updated; carries the post-
   * resistance amount and the damage type. `SpriteManager` consumes this
   * for the A01c hit-flash (brief red tint on the sprite material).
   */
  "combat:damage_taken": {
    instanceId: string;
    amount: number;
    damageType: DamageType;
  };

  /**
   * GF7 — HP restored to an entity (spell heals, potions). `amount` is
   * the effective gain after the max-HP clamp; zero-gain uses are not
   * emitted. Drives the green heal popups and the heal chime.
   */
  "combat:healed": {
    instanceId: string;
    amount: number;
  };

  /**
   * EV-E4 — a condition landed on an entity (enemy attack rider, trap,
   * or spell). Consumed by the feed and future condition-row UI.
   */
  "combat:condition_applied": { targetId: string; conditionId: ConditionId };

  /** EV-E4 — a condition ended early (e.g. Sleep broken by damage). */
  "combat:condition_ended": { targetId: string; conditionId: ConditionId };

  /**
   * Difficulty level changed (Peace → Brutal slider). At the Peace floor
   * enemies never detect, chase, or attack; already-engaged enemies stand
   * down. Emitted by `combat/difficulty.ts`; consumed by the
   * DifficultyChip HUD indicator.
   */
  "combat:difficulty_changed": { level: DifficultyLevel; label: string };

  /**
   * Emitted immediately after a combat event when the defender has one or
   * more enabled reactions whose trigger matches the event (B05). Carries
   * the defender's instanceId and the matched reaction IDs — the reaction
   * effect itself is resolved by downstream tasks (S10 wires spell
   * reactions; physical reactions are stubbed for now).
   */
  "combat:reaction_matched": {
    defenderId: string;
    trigger: string;
    reactionIds: readonly string[];
  };

  // ── narrative:* ───────────────────────────────────────────────────────────
  // Emitted by: NarrativeManager
  // Consumed by: narrativeMachine (XState), DialogueBox (via useInkNarrative)

  /**
   * Kicks off an Ink knot; narrativeMachine transitions to ACTIVE.
   * `speakerInstanceId` is the clicked NPC's sprite id when the knot was
   * launched from a world interaction (N09 uses it to flip the NPC sprite
   * to its TALKING variant). Omitted for plot-trigger / boss-death knots
   * with no visible speaker.
   */
  "narrative:start": { inkKnot: string; speakerInstanceId?: string };

  /** Ink variable changed via `~ var = value`; SessionStore mirrors it. */
  "narrative:variable_changed": InkVariableChange;

  /** Ink story exhausted or forcibly closed; narrativeMachine → INACTIVE. */
  "narrative:ended": Record<string, never>;

  /** Ink called `play_bark(line)` — one-off feed line outside dialogue flow. */
  "narrative:bark": { line: string };

  /** Ink called `open_shop(npc_id)` — inventory system opens shop UI. */
  "narrative:open_shop": { npcId: string };

  /** Ink called `roll_skill(skill, dc)` — future skill-check handler resolves. */
  "narrative:skill_check_requested": { skill: string; dc: number };

  /**
   * A skill check finished resolving (N06 feed contract). Consumed by the
   * feed formatter to render a canonical line, and by any observer that
   * wants to log/record dialogue skill rolls (save-file replay, LLM
   * context).
   */
  "narrative:skill_check_resolved": {
    speakerName: string;
    skill: string;
    dc: number;
    roll: number;
    bonus: number;
    total: number;
    success: boolean;
  };

  // ── inventory:* ───────────────────────────────────────────────────────────
  // Emitted by: InkContract (give_item external), future loot/shop handlers
  // Consumed by: future InventoryStore

  /** Add one or more copies of an item to the party inventory. */
  "inventory:give_item": { itemId: string; qty: number };

  /**
   * An item entered the party's possession through a path that already
   * wrote it into an inventory (loot-all, save restore). Carries no
   * inventory side-effect — observers only (KeyringStore). `give_item`
   * implies `item_acquired`; emitters must not fire both for one grant.
   */
  "inventory:item_acquired": { itemId: string; qty: number };

  /**
   * Party gold changed (earn or spend). `gold` is the new total; `delta` is
   * the signed change (+earned, −spent). Emitted by callers after mutating
   * PartyStore gold so the UI can refresh.
   */
  "inventory:gold_changed": { gold: number; delta: number };

  // ── spell:* ───────────────────────────────────────────────────────────────
  // Emitted by: ConcentrationTracker (S08), CombatManager.castSpell (S22)
  // Consumed by: PartyBar portrait glow, CombatLog, SpellVfx (S24)

  /**
   * S22 — a spell finished resolving. `targets` lists the entity ids that
   * received damage / heal / effect. `cellTarget` is set for AoE casts
   * (Fireball) so the VFX layer knows where to anchor the explosion.
   * Single-target attack/save spells leave it null.
   */
  "combat:spell_cast": {
    casterId: string;
    spellId: string;
    slotLevel: number;
    targets: readonly string[];
    cellTarget: { x: number; z: number } | null;
  };

  /** Caster started concentrating on a spell (prior effect, if any, is dropped). */
  "spell:concentration_started": {
    casterId: string;
    spellId: string;
    slotLevel: number;
  };

  /** Caster's concentration ended. `reason` drives the feed line. */
  "spell:concentration_ended": {
    casterId: string;
    spellId: string;
    reason: "damage" | "new_cast" | "manual" | "incapacitated";
  };

  /** CON save rolled to maintain concentration after taking damage. */
  "spell:concentration_save": {
    casterId: string;
    spellId: string;
    damage: number;
    dc: number;
    d20: number;
    total: number;
    success: boolean;
  };

  // ── enemy:* ───────────────────────────────────────────────────────────────
  // Emitted by: InkContract (set_hostile external), CombatManager, enemyMachine
  // Consumed by: Feed (via handlers), gameMachine, future EnemyManager

  /** Ink called `set_hostile(npc_id)` — flip NPC faction to hostile → combat. */
  "enemy:set_hostile": { npcId: string };

  /** Perception crossed the detection threshold (sight cone or hearing). */
  "enemy:detected": { instanceId: string; name: string };

  /**
   * Enemy entered (`alerted`) or exited (`lost_interest`) the ALERT state
   * after losing a target. Feed renders a line for each phase.
   */
  "enemy:alerted": {
    instanceId: string;
    name: string;
    phase: "alerted" | "lost_interest";
  };

  /**
   * Faction changed at runtime. The canonical trigger in v1 is neutral →
   * hostile ("drawing weapons"); the event carries the full before/after
   * so downstream systems can key off either direction.
   */
  "enemy:faction_flipped": {
    instanceId: string;
    name: string;
    previous: EnemyFaction;
    next: EnemyFaction;
    /** Flavour verb for the feed line (e.g. "draws a cleaver"). Optional. */
    action?: string;
  };

  /** Enemy died. Carries the display name so the feed formatter doesn't need a second lookup. */
  "enemy:died": { instanceId: string; name: string };

  /**
   * A detected enemy roused nearby allies into combat (pack aggro). Emitted
   * once per detection source with the full list of allies it woke, so the
   * feed can render a single alarm line instead of N "notices you" lines.
   */
  "enemy:pack_alerted": {
    byInstanceId: string;
    byName: string;
    instanceIds: readonly string[];
  };

  /**
   * A01b — `enemyMachine` transitioned into a new state value. Consumed
   * by `SpriteManager` to swap animation loops (idle / move / attack).
   * `state` is the raw state value from the machine; the mapper lives
   * in `spriteSchema.animationForEnemyState`.
   */
  "enemy:state_changed": { instanceId: string; state: string };

  /**
   * E12 — a loot pile was spawned at `position` (typically an enemy
   * death cell). The pile is already present in `LootPileStore`; the
   * event is for renderers and the feed formatter.
   */
  "world:loot_dropped": {
    pileId: string;
    position: Position;
    source: string;
  };

  /**
   * Monster(s) spawned at runtime (monster-closet, trigger, or Ink-driven
   * summoning). `source` is a human-readable phrase describing the spawn
   * feature ("the wall", "a rift", "the shadows").
   */
  "enemy:spawned": { instanceId: string; name: string; source: string };

  // ── perception:* ─────────────────────────────────────────────────────────
  // Emitted by: NoiseBroadcast.emitNoise (player movement, weapon swings, etc.)
  // Consumed by: NoiseBroadcast (dispatches to registered enemy callbacks)

  /** A noise event radiates from `cell` up to `radius` cells. */
  "perception:noise": { cell: Position; radius: number };

  /**
   * Player stepped out of the specified enemy's leash, breaking combat
   * for that entity. Feed writes "You disengage.".
   */
  "perception:player_disengaged": { instanceId: string };

  // ── world:* ───────────────────────────────────────────────────────────────
  // Emitted by: NarrativeManager (triggers), future door/trap handlers
  // Consumed by: main.tsx, WorldStore

  /** A trigger object was stepped on; inkKnot was fired. */
  "world:trigger_fired": { triggerId: string };

  /**
   * E16 — the player tried to walk into a cell occupied by an entity.
   * `targetCell` is the cell the player attempted to enter.
   * Subscribers (NPC bump handler) route through the same dispatcher
   * a click on the sprite would take.
   */
  "world:bump_entity": { targetCell: Position };

  /** A door was opened or closed; MiniMap and renderer react. */
  "world:door_state_changed": { doorId: string; open: boolean };

  /**
   * W21b — an interact object (lever, button, pressure plate) just fired.
   * `PropManager` consumes this to swap from the `idle` to the `activated`
   * texture; the feed can surface a flavor line.
   */
  "world:interact_activated": {
    interactId: string;
    cell: Position;
    effectKind: "teleport" | "open_door" | "fire_trigger" | "toggle_light" | "open_camp_stash" | "no_op";
  };

  // ── Floor-decal world objects (W18) ────────────────────────────────────────

  /**
   * A trap was just discovered (Perception success or step-trigger reveal).
   * `FloorDecalManager` swaps from hidden → revealed-armed on this event.
   */
  "world:trap_revealed": { trapId: string; cell: Position };

  /**
   * A trap fired and is now spent. Carries the victim's instanceId when the
   * step-on trigger picked one; omitted for AoE / non-targeted variants.
   */
  "world:trap_triggered": { trapId: string; cell: Position; victimId?: string };

  /** Player disarmed a revealed trap; renderer swaps to the disarmed state. */
  "world:trap_disarmed": { trapId: string };

  /** A container was opened for the first time (P16). Idempotent — fires once per container. */
  "world:container_opened": { containerId: string; cell: Position };

  /** A container's contents were taken; renderer swaps to the `open-empty` state (P16). */
  "world:container_looted": { containerId: string };

  /** I16 — container was unlocked (via key, lockpick, or force open). */
  "world:container_unlocked": { containerId: string };

  /** I16 — door was unlocked (via key, lockpick, or force open). */
  "world:door_unlocked": { doorId: string };

  /** A new pile of items appeared on the floor (P16). */
  "world:item_drop_added": {
    dropId: string;
    cell: Position;
    items: readonly string[];
  };

  /** A floor drop was picked up; renderer removes the glint. */
  "world:item_drop_picked_up": { dropId: string };

  /**
   * A ceiling light was toggled (W22). `off` is the *resulting* state —
   * `true` means the light is now dark. `LightRenderer` flips
   * `PointLight.visible`; `NarrativeManager` pushes
   * `light_<id>_off` into the observed Ink VAR table.
   */
  "world:light_toggled": { lightId: string; off: boolean };

  /**
   * Player stepped onto a teleporter. Carries the resolved destination so
   * the renderer / feed / save layer don't need to re-look it up.
   */
  "world:teleporter_used": {
    teleporterId: string;
    fromCell: Position;
    targetMap: string;
    targetCell: Position;
  };

  /** Player stepped onto a stairs cell. */
  "world:stairs_used": {
    stairsId: string;
    direction: "up" | "down";
    targetMap: string;
    targetCell: Position;
  };

  /**
   * A previously hidden secret door was discovered (W12). The renderer
   * paints the reveal cue, the feed announces the discoverer, and from
   * this point the cell behaves as a normal door (open via doorId).
   */
  "world:secret_door_revealed": {
    doorId: string;
    discovererName: string;
    dc: number;
    passive: number;
  };

  /** Ink called `give_xp(amount)` — award XP to the party. */
  "world:give_xp": { amount: number };

  /**
   * XP was actually awarded to the party (W07). `source` is the kill's
   * `instanceId` for monster drops, or a Ink-knot identifier for quest
   * grants. Consumers: feed renderer, level-up resolver, future analytics.
   */
  "world:xp_awarded": { amount: number; source: string };

  /** Active floor changed; HUD updates the label and resizes the minimap canvas (W13/W14). */
  "world:map_name_changed": { displayName: string; width: number; height: number };

  /** Long rest finished at a camp; persistence autosaves (P05). */
  "world:long_rest_completed": { memberIds: readonly string[] };

  /** Player entered a new floor / map. Payload carries the new floor id. */
  "world:floor_entered": { floorId: string };

  /** Ink `quest_*` variable flipped to a truthy terminal state (P05). */
  "world:quest_completed": { questId: string };

  /**
   * A party member crossed an XP threshold and advanced to a new level (W09).
   * One emission per level gained; a large XP jump fires multiple events in
   * order. `asiRequired` lets the HUD queue the ASI modal.
   */
  "world:level_up": {
    instanceId: string;
    name: string;
    newLevel: number;
    hpGained: number;
    proficiencyAfter: number;
    slotsAdded: Readonly<Record<number, number>>;
    asiRequired: boolean;
  };

  /** Every party member is dead (failures ≥ 3). UI shows the wipe modal (P09). */
  "world:party_wiped": Record<string, never>;

  /** gameMachine entered `transitioning`; UI should animate a fade-out overlay. */
  "world:fade_out": Record<string, never>;

  /** gameMachine left `transitioning`; UI should animate the fade-out overlay away. */
  "world:fade_in": Record<string, never>;

  // ── save:* ────────────────────────────────────────────────────────────────
  // Emitted by: InkContract (autosave external), future persistence system
  // Consumed by: future SaveManager

  /** Ink called `autosave()` — persistence system should write a checkpoint. */
  "save:autosave_requested": Record<string, never>;

  // ── ui:* ──────────────────────────────────────────────────────────────────
  // Emitted by: CombatManager, main.tsx (movement, init)
  // Consumed by: useCombatLog, MiniMap, PartyBar

  /** Push one line into the scrolling combat/event feed. */
  "ui:combat_log_push": { message: string };

  /** Player grid cell changed; MiniMap redraws. */
  "ui:player_moved": { position: Position };

  /** Party HP/status changed; PartyBar refreshes. */
  "ui:party_update": Record<string, never>;

  /**
   * I15b — ask the HUD to open the container loot modal for this container.
   * Emitted by the main.tsx container-open rule after a successful open,
   * and also on re-click of an already-opened-but-unlooted container so
   * the player can return to a half-looted chest. Consumed by
   * `ContainerLootModal`, which reads the stashed floor drop from
   * `WorldStore.getFloorDrops()`.
   */
  "ui:container_open": { containerId: string; cell: Position };

  /**
   * I16 — ask the HUD to open the lock menu for a locked container or door.
   * Consumed by `LockMenu`; its buttons ("Use Key" / "Force Open" / "Cancel")
   * drive `WorldStore.markContainerUnlocked` / `markDoorUnlocked` or cancel.
   * I17 owns the lockpicking + force-open resolvers; in I16 "Force Open"
   * is a disabled stub.
   */
  "ui:lock_menu_open": {
    targetKind: "container" | "door";
    targetId: string;
    lockDc: number;
    keyItemId?: string;
  };

  /**
   * U21 — player clicked a hand button on a party card.
   * main.tsx resolves the nearest valid target and calls playerAttack.
   */
  "ui:hand_action": { characterId: string; hand: "mainHand" | "offHand" };

  /**
   * U26 — player clicked a floor loot pile; show the pickup modal.
   * `pileId` matches a `LootPile` in `LootPileStore`.
   */
  "ui:loot_pile_open": { pileId: string };

  /** U27 — open the camp stash screen (triggered from a camp interact object). */
  "ui:camp_stash_open": Record<string, never>;

  /**
   * Party stepped onto a `camp` cell and passed the hostile-proximity
   * check; the HUD shows the long-rest confirm prompt (C08 wiring).
   */
  "ui:camp_prompt": { cell: Position };

  /**
   * Toggle PartyBar's consumable-slot row. Emitted by the NavCluster's
   * potion button so the toggle lives in the touch nav strip instead of
   * a floating header above the party cards.
   */
  "ui:toggle_consumables": Record<string, never>;

  /**
   * S20 — player tapped a hand holding a spellbook/holy-symbol.
   * Opens the SpellChooser modal for that caster. Both hands route to
   * the same chooser; the spellbook is just the cast trigger.
   */
  "ui:open_spell_chooser": { casterId: string; hand: "mainHand" | "offHand" };

  /**
   * S23 — chooser → engine handoff. main.tsx listens, loads the spell,
   * arms the SpellTargetingDispatcher, then re-emits `ui:spell_armed`
   * so the HUD toast can render. `slotLevel` is captured at chooser-pick
   * time so an upcast UI (S08c) can override it later without changing
   * the targeting protocol.
   */
  "ui:arm_spell": {
    casterId: string;
    spellId: string;
    slotLevel: number;
  };

  /**
   * S23 — a spell is now armed and waiting for a target click. The HUD
   * shows a small toast/banner with the spell name + how to cancel; S26
   * minimap targeting reads the team/range/multi-count metadata to paint
   * eligibility.
   */
  "ui:spell_armed": {
    casterId: string;
    casterName: string;
    spellId: string;
    spellName: string;
    targeting: "single" | "aoe" | "multi" | "touch";
    targetTeam: "ally" | "enemy" | "any";
    rangeCells: number;
    /** `multi` only — Confirm enables when picks ≥ minTargets. */
    minTargets?: number;
    /** `multi` only — auto-finalize at this count. */
    maxTargets?: number;
    /** Revive-class spells (Raise Dead) flip this to `true`. */
    includeDead: boolean;
    /** S26 battle-board orientation: party cell + forward direction. */
    casterPos: Position;
    casterFacing: Direction;
  };

  /**
   * S23 — armed spell was committed or cancelled; the HUD clears its toast.
   * `reason` is "committed" on a successful target pick, "cancelled" on
   * Escape or outside-click.
   */
  "ui:spell_disarmed": { reason: "committed" | "cancelled" };
}

// ---------------------------------------------------------------------------
// Namespace-scoped type helpers
// ---------------------------------------------------------------------------

/** All event keys that belong to a given namespace prefix. */
export type EventKeysInNamespace<NS extends EventNamespace> = Extract<
  keyof EngineEvents,
  `${NS}:${string}`
>;

/** Subset of EngineEvents for a single namespace (useful for mock/test setup). */
export type NamespacedEvents<NS extends EventNamespace> = Pick<
  EngineEvents,
  EventKeysInNamespace<NS>
>;

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

type Listener<T> = (payload: T) => void;

/**
 * Minimal typed event bus shared by the engine and UI hooks.
 * Keeps rendering, combat math, and XState actors decoupled.
 */
class EventBusImpl {
  private readonly listeners = new Map<string, Set<Listener<unknown>>>();

  on<K extends keyof EngineEvents>(event: K, listener: Listener<EngineEvents[K]>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    const set = this.listeners.get(event)!;
    set.add(listener as Listener<unknown>);
    return () => {
      set.delete(listener as Listener<unknown>);
    };
  }

  emit<K extends keyof EngineEvents>(event: K, payload: EngineEvents[K]): void {
    if (import.meta.env.DEV) {
      console.log(`[bk] event ${event}`, payload);
    }
    const set = this.listeners.get(event);
    if (!set) return;
    for (const listener of set) {
      listener(payload);
    }
  }
}

/** Singleton event bus — import this everywhere. */
export const EventBus = new EventBusImpl();
